import { isArray } from "../utils/utils"
import { formatMessage, translate } from "./providers"
import { LangFile, Message } from "./types"

/// <reference path="providers.ts" />

/**
 * Type describing a message
 */
export type MsgObject = MsgWithContext | MsgText

export interface MsgWithContext {
    id: string
    context: string
}

interface MsgText {
    text: string
}


/**
 * Returns a translated message
 */
export function msg(id: string | MsgObject, locals?: MsgTemplateArguments) {
    let msg = translate(id)
    msg = formatMessage(msg, locals)
    return msg
}

/**
 * Returns a "constant" message (i.e., not to be translated, such as a brand name)
 */
export function msgText(text: string): MsgText {
    return {
        text
    }
}

/**
 * Transforms a set of messages to include a context
 */
export function msgSet<Key>(messages: { [key in keyof Key]: string }, context: string) {

    const r = Object.entries(messages).reduce((state, [id, text]) => {

        state[id] = {
            id: text,
            context,
            toString: () => text
        }


        return state

    }, {})

    return r as { [key in keyof Key]: MsgWithContext }

}

/**
 * Checks if the argument is a message with context
 */
function isMessageWithContext(msg: any): msg is MsgWithContext {

    if (typeof msg === "object") {
        return Boolean(msg["id"])
    }

}

/**
 * Checks if the argument is a message with context
 */
function isTranslatedMessage(msg: any): msg is MsgText {

    if (typeof msg === "object") {
        return Boolean(msg["text"])
    }

}

export type MsgTemplateArguments = Record<string, string | number>

const MsgTemplatePrefix = "{}"

interface ParsedMessage {
    id: string,
    context?: string,
    locals?: MsgTemplateArguments,
    text?: string
}

/**
 * Produces a message template along with its list of variables
 * Such templates are currently strings with a reserved prefix ("MsgTemplatePrefix")
 * (not exactly reserved, but very unlikely to be ever used in a message displayed to users),
 * and including their variable parts in JSON
 */
export function msgTmpl(template: string, locals: MsgTemplateArguments): string {
    return MsgTemplatePrefix + JSON.stringify([
        template,
        locals
    ])
}

export function parseMessage(msg: string | MsgObject): ParsedMessage {

    if (!msg) {
        return { id: undefined }
    }

    if (isMessageWithContext(msg)) {
        return msg
    }

    if (isTranslatedMessage(msg)) {
        return { id: undefined, text: msg.text }
    }

    if (typeof msg === "string") {

        msg = msg.trim()

        if (msg?.startsWith(MsgTemplatePrefix)) {

            msg = msg.substring(MsgTemplatePrefix.length)

            try {

                const parsed = JSON.parse(msg) as ParsedMessage

                if (isArray(parsed) && parsed.length <= 2) {

                    return {
                        id: parsed[0],
                        locals: parsed[1] as unknown as MsgTemplateArguments
                    }

                }

            }
            catch (error) {
                console.error("Error parsing message", { msg, error })
            }

        }


    }

    return { id: msg.toString() }

}

export function loadMessages(messages: LangFile["messages"]): Map<string, Message> {

    const tmp: [string, Message][] = messages
        .filter(m => m.id)
        .map(m => [
            getMessageKey({ context: m.context, id: normalizeWhitespace(m.id) }),
            m
        ])

    const map = new Map(tmp)

    return map

}


export function normalizeWhitespace(text: string) {
    if (!text) return undefined
    text = text.replace(/[\r\n]+/g, "")
    text = text.replace(/\s{2,}/g, " ")
    return text
}

const keySeparator = "::"

export function getMessageKey({ context, id }: { context?: string, id: string }) {
    return `${(context ?? "")}${keySeparator}${id}`
}
